import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "react-query";
import { useSnackbar } from "notistack";
import {
  getConversations,
  getConversationMessages,
  deleteConversation,
  updateConversationTitle,
  ConversationItem,
} from "../api-services/conversation";
import { QUERY_KEY } from "../commons/query-key";

export const useConversationList = (agencyId?: string, enabled = true) => {
  const queryResult = useInfiniteQuery(
    [QUERY_KEY.CONVERSATION_LIST, agencyId],
    ({ pageParam }) => getConversations({ cursor: pageParam, limit: 20 }, agencyId),
    {
      enabled,
      getNextPageParam: (lastPage: any) => {
        const data = lastPage?.data ?? lastPage;
        return data?.nextCursor ?? undefined;
      },
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    }
  );

  const conversations: ConversationItem[] =
    queryResult.data?.pages?.flatMap((page: any) => {
      const data = page?.data ?? page;
      return data?.conversations ?? [];
    }) ?? [];

  return {
    ...queryResult,
    conversations,
  };
};

export const useConversationMessages = (conversationID: string | null, agencyId?: string) => {
  return useQuery(
    [QUERY_KEY.CONVERSATION_MESSAGES, conversationID, agencyId],
    () => getConversationMessages(conversationID!, { limit: 50 }, agencyId),
    {
      enabled: !!conversationID,
      refetchOnWindowFocus: false,
    }
  );
};

export const useDeleteConversation = (agencyId?: string) => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation((conversationID: string) => deleteConversation(conversationID, agencyId), {
    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_KEY.CONVERSATION_LIST, agencyId]);
      enqueueSnackbar("Conversation deleted", { variant: "success" });
    },
    onError: () => {
      enqueueSnackbar("Failed to delete conversation", { variant: "error" });
    },
  });
};

export const useUpdateConversationTitle = (agencyId?: string) => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ conversationID, title }: { conversationID: string; title: string }) =>
      updateConversationTitle(conversationID, title, agencyId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([QUERY_KEY.CONVERSATION_LIST, agencyId]);
      },
    }
  );
};
